import { useEffect, useState, type FormEvent } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import {
  fetchAllFaqs,
  saveFaq,
  deleteFaq,
  reorderFaqs,
  type Faq,
  type FaqInput,
} from '@/services/faqs';

const emptyFaq = (order: number): FaqInput => ({
  question: '',
  answer: '',
  category: 'General',
  displayOrder: order,
  isActive: true,
});

export default function AdminFaqPage() {
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [editing, setEditing] = useState<FaqInput | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      setFaqs(await fetchAllFaqs());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar las preguntas.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    setFormError('');
    const { error: err } = await saveFaq(editing);
    setSaving(false);
    if (err) {
      setFormError(err);
      return;
    }
    setEditing(null);
    setFeedback('Pregunta guardada correctamente.');
    await load();
  }

  async function handleDelete(id: string) {
    const { error: err } = await deleteFaq(id);
    if (err) {
      setError(err);
      return;
    }
    setFeedback('Pregunta eliminada.');
    await load();
  }

  async function move(id: string, dir: -1 | 1) {
    const idx = faqs.findIndex((f) => f.id === id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= faqs.length) return;
    const reordered = [...faqs];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    setFaqs(reordered);
    await reorderFaqs(reordered.map((f) => f.id));
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[900px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Preguntas Frecuentes</h1>
            <p className="text-sm text-foreground-500 mt-0.5">
              Estas preguntas se muestran en el home de la tienda, entre el banner y el catálogo.
            </p>
          </div>
          <button
            onClick={() => {
              setEditing(emptyFaq(faqs.length + 1));
              setFormError('');
            }}
            className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-add-line mr-1.5"></i>Nueva pregunta
          </button>
        </div>

        {feedback && (
          <div className="flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg px-4 py-2.5">
            <i className="ri-check-line"></i>{feedback}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-3 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-2.5">
            <i className="ri-error-warning-line"></i>{error}
            <button onClick={load} className="ml-auto text-xs font-medium underline whitespace-nowrap cursor-pointer">Reintentar</button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-foreground-400">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i>Cargando...
          </div>
        ) : faqs.length === 0 ? (
          <div className="bg-white rounded-xl border border-background-200 text-center py-16 text-foreground-500">
            <i className="ri-question-line text-4xl block mb-3"></i>
            <p className="text-sm">Aún no hay preguntas. Crea la primera.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={faq.id} className="bg-white rounded-xl border border-background-200 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex flex-col gap-1 pt-0.5">
                    <button
                      onClick={() => move(faq.id, -1)}
                      disabled={idx === 0}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 disabled:opacity-30 cursor-pointer"
                      aria-label="Subir"
                    >
                      <i className="ri-arrow-up-line"></i>
                    </button>
                    <button
                      onClick={() => move(faq.id, 1)}
                      disabled={idx === faqs.length - 1}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 disabled:opacity-30 cursor-pointer"
                      aria-label="Bajar"
                    >
                      <i className="ri-arrow-down-line"></i>
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-semibold text-accent-600 uppercase tracking-wide">{faq.category}</span>
                      {!faq.isActive && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">Oculta</span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-foreground-900 mt-1">{faq.question}</p>
                    <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{faq.answer}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() =>
                        setEditing({
                          id: faq.id,
                          question: faq.question,
                          answer: faq.answer,
                          category: faq.category,
                          displayOrder: faq.displayOrder,
                          isActive: faq.isActive,
                        })
                      }
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-accent-600 hover:bg-accent-100 transition-colors cursor-pointer"
                      aria-label="Editar"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(faq.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                      aria-label="Eliminar"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <Modal
          title={editing.id ? 'Editar pregunta' : 'Nueva pregunta'}
          subtitle="Se muestra en el home de la tienda pública."
          onClose={() => setEditing(null)}
          maxWidth="max-w-xl"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                <i className="ri-error-warning-line"></i>{formError}
              </div>
            )}
            <div>
              <label className={labelCls}>Categoría / tema</label>
              <input
                className={inputCls}
                value={editing.category}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                placeholder="Ej: Garantías, Envíos, Cambios..."
              />
            </div>
            <div>
              <label className={labelCls}>Pregunta *</label>
              <input
                className={inputCls}
                value={editing.question}
                onChange={(e) => setEditing({ ...editing, question: e.target.value })}
                placeholder="¿Cómo funciona...?"
              />
            </div>
            <div>
              <label className={labelCls}>Respuesta *</label>
              <textarea
                className={`${inputCls} resize-none`}
                rows={4}
                maxLength={500}
                value={editing.answer}
                onChange={(e) => setEditing({ ...editing, answer: e.target.value })}
                placeholder="Escribe la respuesta para el cliente..."
              />
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={editing.isActive}
                onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
                className="w-4 h-4 accent-accent-500 cursor-pointer"
              />
              <span className="text-sm text-foreground-700">
                {editing.isActive ? 'Visible en la tienda' : 'Oculta'}
              </span>
            </label>
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </AdminLayout>
  );
}